<div class="side-questionnaire" data-element="side-questionnaire" [attr.data-side]="this.side">

  <!-- Collapsible header (Calls & Puts wide tab only). The WHOLE row is the collapse control
       (WI-12): a big centred side title over its column — "click on calls, click on puts".
       The side name stands alone: no "collapse" hint text (owner, WI-12 review) — the hover
       state is the affordance. Collapse is VISUAL ONLY — inputs stay intact and still gate
       the Run (spec §5). -->
  <div class="side-header"
       *ngIf="this.collapsible && !this.isCollapsed"
       data-element="side-collapse-toggle"
       title="Collapse This Side (Inputs Are Kept)"
       (click)="this.onToggleCollapsed()">
    <span class="side-title">{{ this.sideName }}</span>
  </div>

  <!-- Collapsed = only the header, rendered as the frame-06 vertical rail; click-to-expand.
       Bare side name — the "collapsed" word dropped with the header hint (owner, WI-12 review);
       the rail's own shape says it. The incomplete badge stays (owner-kept). -->
  <div class="side-rail"
       *ngIf="this.isCollapsed"
       data-element="side-collapse-toggle"
       title="Expand This Side"
       (click)="this.onToggleCollapsed()">
    <span class="rail-text">{{ this.sideName }}</span>
    <span class="rail-incomplete" *ngIf="!this.isComplete">Incomplete</span>
  </div>

  <ng-container *ngIf="!this.isCollapsed">
    <ets-hedge-preset-selector [underlying]="presetUnderlying || targetInstrument?.ticker" [side]="side" [apgSource]="presetApgSource" [selection]="inputs" [disabled]="disabled" (changed)="onPresetChanged()"></ets-hedge-preset-selector>
    <label *ngIf="inputs?.mode === 'preset'">Anchor ATM
      <ets-strikes-dropdown [targetInstrument]="targetInstrument" [expiration]="strikeExpiration"
        [strike]="inputs.presetAnchor" [disable]="disabled" [showClearButton]="true"
        (strikeChange)="onPresetAnchorChanged($event)"></ets-strikes-dropdown>
      <span *ngIf="presetAnchorInvalid" class="preset-anchor-error" role="alert">Anchor Leg Must Be a Multiple of 10.</span>
    </label>
    <ng-container *ngIf="inputs?.mode !== 'preset'">

    <!-- 1. Price Range — 3-mode segmented group; selector row, then the mode's inputs
         on their OWN row (frame-02 pattern, every instantiation — wide tab included).
         No "— incomplete" legend hints anywhere (owner smoke 2026-07-10, HedgeMatrix parity). -->
    <fieldset class="fieldset" data-element="fieldset-price-range">
      <legend class="fieldset-title">Price Range</legend>

      <!-- CR-20260831-004: .param-changed = this control no longer matches what the tab's last
           run sent — the SELECTED segment paints yellow instead of blue (value inputs get the
           yellow background instead). Cleared by re-running, reverting, or the refresh reset. -->
      <!-- [selectedItems] paired with the keys (CR-20260831-004 defect round 2): this group's
           items rebuild on the availability gate, and ButtonGroup then re-feeds its stale
           self-synced selection objects — the mutual-exclusivity highlight bug. Supplying the
           CURRENT selected object keeps the highlight single and present. Same on the anchor
           group below (its items rebuild on every chosen-ATM tick). -->
      <dx-button-group
        [class.param-changed]="this.isChanged('priceRange.kind')"
        [items]="this.priceRangeButtons"
        keyExpr="id"
        selectionMode="single"
        stylingMode="contained"
        [disabled]="this.disabled"
        [selectedItemKeys]="[this.priceKind]"
        [selectedItems]="this.priceRangeSelectedItems"
        (onItemClick)="this.onPriceKind($event)"
      ></dx-button-group>

      <!-- Strike pickers = the platform ets-strikes-dropdown (owner smoke 2026-07-10 —
           HedgeMatrix parity, never plain number boxes), fed the NEAREST expiration. -->
      <div class="field" *ngIf="this.priceKind === 'strikes'">
        <div class="label">Start / End Strike</div>
        <div class="pair">
          <ets-strikes-dropdown data-element="pr-strikes-start"
                                [class.param-changed]="this.isChanged('startStrike')"
                                [targetInstrument]="this.targetInstrument"
                                [expiration]="this.strikeExpiration"
                                [(strike)]="this.startStrike" [showClearButton]="true"
                                [disable]="this.disabled"
                                (strikeChanged)="this.onStrikePicked()"></ets-strikes-dropdown>
          <ets-strikes-dropdown data-element="pr-strikes-end"
                                [class.param-changed]="this.isChanged('endStrike')"
                                [targetInstrument]="this.targetInstrument"
                                [expiration]="this.strikeExpiration"
                                [(strike)]="this.endStrike" [showClearButton]="true"
                                [disable]="this.disabled"
                                (strikeChanged)="this.onStrikePicked()"></ets-strikes-dropdown>
        </div>
      </div>

      <!-- Follow-Through (CR-20260727-002): the START STRIKE is the only input. The Up | Down
           selector is GONE — calls only run up and puts only run down, so the pair offered a
           choice that does not exist and let a puts side sit on "Up" (owner screencast
           2026-07-27). What replaces it STATES the coverage: same half-row footprint the two
           buttons had (.pair gives every child flex: 1 1 0), rounded like a button, but inert —
           "it could be a button, but not a clickable button" (owner).
           It is a STATEMENT, not a blocked input, so it must not be painted as disabled either.
           Same reasoning as Expected Range below, which has never had a direction control. -->
      <div class="field" *ngIf="this.priceKind === 'followThrough'">
        <div class="label">Start Strike</div>
        <div class="pair">
          <ets-strikes-dropdown data-element="pr-follow-start"
                                [class.param-changed]="this.isChanged('startStrike')"
                                [targetInstrument]="this.targetInstrument"
                                [expiration]="this.strikeExpiration"
                                [(strike)]="this.startStrike" [showClearButton]="true"
                                [disable]="this.disabled"
                                (strikeChanged)="this.onStrikePicked()"></ets-strikes-dropdown>
          <!-- No tabindex, no (click), no role: nothing here is interactive, so it stays out of
               the tab order and announces as the plain text it is. -->
          <div class="statement" data-element="pr-follow-direction">{{ this.followThroughStatement }}</div>
        </div>
      </div>

      <!-- Expected Range (CR-20260715-001): the START STRIKE is the ONLY input. Deliberately
           NO direction control (calls only cover up, puts only down — the side implies it)
           and NO end/value input (the per-date range end is the SKILL's own calculation —
           the frontend couriers the fact). -->
      <div class="field" *ngIf="this.priceKind === 'expectedRange'">
        <div class="label">Start Strike</div>
        <ets-strikes-dropdown data-element="pr-expected-start"
                              [class.param-changed]="this.isChanged('startStrike')"
                              [targetInstrument]="this.targetInstrument"
                              [expiration]="this.strikeExpiration"
                              [(strike)]="this.startStrike" [showClearButton]="true"
                              [disable]="this.disabled"
                              (strikeChanged)="this.onStrikePicked()"></ets-strikes-dropdown>
        <div class="hint ft-hint">{{ this.expectedRangeText }}</div>
      </div>

      <!-- ATM Offset (WI-4, revised 2026-07-13): the Chosen pill shows its strike value, Market
           is a bare label; Offset From ATM + the range are NUMERIC inputs stepping by the strike
           step (a negative offset is in-the-money); Follow-Through shows a side-aware coverage
           line instead of an input. -->
      <!-- .atm-anchor-field: the WI-15 stale cue paints the CHOSEN pill (the first segment of THIS
           group only), so the group needs a handle of its own — the sibling fields carry
           button-groups too. -->
      <div class="field atm-anchor-field" *ngIf="this.priceKind === 'atmOffset'">
        <dx-button-group
          [class.param-changed]="this.isChanged('atm.anchor')"
          [items]="this.atmAnchorButtons"
          keyExpr="id"
          selectionMode="single"
          stylingMode="contained"
          [disabled]="this.disabled"
          [selectedItemKeys]="[this.atmAnchor]"
          [selectedItems]="this.atmAnchorSelectedItems"
          (onItemClick)="this.onAtmAnchor($event)"
        ></dx-button-group>

        <div class="labeled-pair">
          <span class="label">Offset From ATM</span>
          <dx-number-box class="grow" data-element="atm-offset-signed"
                         [class.param-changed]="this.isChanged('atm.offset')"
                         [(value)]="this.atmOffset" [step]="this.strikeStep"
                         format="$#,##0" [showSpinButtons]="true" [showClearButton]="true"
                         [disabled]="this.disabled" placeholder="Offset From ATM"
                         (onValueChanged)="this.onAtmOffsetEdited($event)"></dx-number-box>
        </div>
        <div class="hint">{{ this.atmOffsetSignHint }}</div>

        <div class="labeled-pair dtc-row">
          <span class="label">Distance To Cover</span>
          <dx-button-group class="grow"
            [class.param-changed]="this.isChanged('atm.subKind')"
            [items]="this.atmSubKindButtons"
            keyExpr="id"
            selectionMode="single"
            stylingMode="contained"
            [disabled]="this.disabled"
            [selectedItemKeys]="[this.atmSubKind]"
            (onItemClick)="this.onAtmSubKind($event)"
          ></dx-button-group>
        </div>

        <!-- Range value for the bounded subs (Range | Both), Both CR: label + the static $ box
             + the "Use Expected Range" checkbox (the dynamic choice — vrequest 2026-07-15: a
             checkbox, NOT sub-buttons). Checked = the skill's per-date expected range sizes the
             range family, so the $ box is BLOCKED and CLEARED (owner smoke 2026-07-15 — a number
             that no longer travels must not sit in the box); unchecked re-enables it empty. -->
        <div class="labeled-pair range-value-row" *ngIf="this.atmSubNeedsRangeValue">
          <span class="label">Provide Range To Cover</span>
          <div class="grow inline">
            <dx-number-box class="grow" data-element="atm-offset-extent"
                           [class.param-changed]="this.isChanged('atm.extent')"
                           [(value)]="this.atmExtent" [step]="this.strikeStep" [min]="0"
                           format="$#,##0" [showSpinButtons]="true" [showClearButton]="true"
                           [disabled]="this.disabled || this.atmUseExpectedRange" placeholder="Range Distance"
                           (onValueChanged)="this.onAtmExtentEdited($event)"></dx-number-box>
            <!-- CR-20260727-001: the second Expected Range entry point. Greys out WITH the pill,
                 never separately — both reduce to the same backend boundary math, so leaving this
                 one live would be a route to a mode the settings say is unavailable. Disabled in
                 place rather than hidden (owner ruling 2026-07-27 R2), and hinted, because the
                 cause lives in a different dialog. -->
            <dx-check-box text="Use Expected Range" data-element="chk-atm-expected-range"
                          [class.param-changed]="this.isChanged('atm.useExpectedRange')"
                          [(value)]="this.atmUseExpectedRange"
                          [disabled]="this.disabled || !this.expectedRangeAvailable"
                          [hint]="this.expectedRangeAvailable ? '' : this.expectedRangeUnavailableHint"
                          (onValueChanged)="this.onAtmUseExpectedRangeEdited($event)"></dx-check-box>
          </div>
        </div>

        <div class="hint ft-hint" *ngIf="this.atmSubKind === 'range' && this.atmUseExpectedRange">{{ this.atmExpectedRangeText }}</div>

        <div class="hint ft-hint" *ngIf="this.atmSubKind === 'both'">{{ this.atmBothText }}</div>

        <div class="hint ft-hint" *ngIf="this.atmSubKind === 'followThrough'">{{ this.followThroughText }}</div>
      </div>
    </fieldset>

    <!-- 2. Minimum Profit — the input MODE is a two-button selector (vrequest 2026-07-20). It
         REPLACED a "Normalized" checkbox outright: the modes are mutually exclusive and decide
         what the SAME typed number means, which a checkbox stated only on its checked side.
         Normalized is the DEFAULT (owner ruling) — it is also the mode that needs no Default Qty,
         whereas Raw derives W_pts = amount / (qty * 100) and drops the whole side when qty is
         missing (OpgRunNormalizer, skip reason NoDefaultQtyForMinProfit).
         The $ format is kept in BOTH modes (owner ruling: a normalized value is still a dollar
         amount, just per unit). Selector then input on its own row — no .inline wrapper: the
         fieldset is already a flex column, and the button group is full-width by the shared
         .dx-buttongroup-wrapper rule, so both stretch without extra styling. -->
    <fieldset class="fieldset" data-element="fieldset-min-profit">
      <legend class="fieldset-title">Minimum Profit</legend>

      <dx-button-group
        [class.param-changed]="this.isChanged('minProfit.mode')"
        [items]="this.minProfitModeButtons"
        keyExpr="id"
        selectionMode="single"
        stylingMode="contained"
        [disabled]="this.disabled"
        [selectedItemKeys]="[this.minProfitMode]"
        (onItemClick)="this.onMinProfitMode($event)"
      ></dx-button-group>

      <dx-number-box data-element="min-profit-amount"
                     [class.param-changed]="this.isChanged('minProfit.amount')"
                     [(value)]="this.minProfitAmount" placeholder="Minimum Profit Target"
                     format="$#,##0.00" [min]="0" [showClearButton]="true" [showSpinButtons]="true"
                     [disabled]="this.disabled"
                     (onValueChanged)="this.onMinProfitEdited($event)"></dx-number-box>
    </fieldset>

    <!-- 3. Max Risk — Dollar ($) | Relative; Relative renders EXACTLY
         Low | Moderate | Aggressive | All (§10 — never High/Conservative/Auto).
         The WHOLE fieldset exists only while the side's coverage has a brake to size:
         follow-through-only coverage (main mode, or ATM Offset + Follow-Through sub) REMOVES
         it — never a greyed-out state — because the question has no answer there
         (hide-risk-for-follow-through, meeting 2026-07-15 pkg-03, owner 00:43–00:44). -->
    <fieldset class="fieldset" data-element="fieldset-max-risk" *ngIf="this.maxRiskVisible">
      <legend class="fieldset-title">Max Risk</legend>

      <dx-button-group
        [class.param-changed]="this.isChanged('maxRisk.kind')"
        [items]="this.maxRiskButtons"
        keyExpr="id"
        selectionMode="single"
        stylingMode="contained"
        [disabled]="this.disabled"
        [selectedItemKeys]="[this.maxRiskKind]"
        (onItemClick)="this.onMaxRiskKind($event)"
      ></dx-button-group>

      <div class="field" *ngIf="this.maxRiskKind === 'dollar'">
        <div class="label">Maximum Risk in Dollars</div>
        <div class="inline">
          <dx-number-box class="grow" data-element="risk-dollar-amount"
                         [class.param-changed]="this.isChanged('maxRisk.amount')"
                         [(value)]="this.dollarAmount"
                         format="$#,##0.00" [min]="0" [showClearButton]="true" [showSpinButtons]="true"
                         [disabled]="this.disabled"
                         (onValueChanged)="this.onDollarRiskEdited($event)"></dx-number-box>
          <!-- Stays a CHECKBOX while Minimum Profit above became a two-button selector. The
               asymmetry is DELIBERATE (owner ruling, vrequest 2026-07-20), not drift: a matching
               "Normalized (Def. Qty) | Raw ($)" pair here would put "Raw ($)" one row beneath the
               "Dollar ($)" selector above — two unrelated meanings of $ stacked together
               ($ = a dollar ceiling rather than a relative level, vs $ = position-total rather
               than per-unit). Default is CHECKED, so both inputs still agree on Normalized.
               Do NOT "harmonize" this control with the one above. -->
          <dx-check-box text="Normalized" data-element="chk-risk-normalized"
                        [class.param-changed]="this.isChanged('maxRisk.normalized')"
                        [(value)]="this.dollarNormalized" [disabled]="this.disabled"
                        (onValueChanged)="this.onDollarNormalizedEdited($event)"></dx-check-box>
        </div>
      </div>

      <dx-button-group
        *ngIf="this.maxRiskKind === 'relative'"
        [class.param-changed]="this.isChanged('maxRisk.level')"
        [items]="this.relativeLevelButtons"
        keyExpr="id"
        selectionMode="single"
        stylingMode="contained"
        [disabled]="this.disabled"
        [selectedItemKeys]="[this.relativeLevel]"
        (onItemClick)="this.onRelativeLevel($event)"
      ></dx-button-group>

      <!-- Both CR: with a mixed run the levels shape only the range family (owner 00:28) —
           said here once, so a follow-through card without a Max Risk line reads as designed. -->
      <div class="hint" *ngIf="this.bothRiskScopeVisible" data-element="both-risk-scope-hint">{{ this.bothRiskScopeHint }}</div>
    </fieldset>

    </ng-container>
  </ng-container>

</div>
